import React, { useState, useEffect, useRef, useCallback } from 'react';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { getParks, getParkDetail, saveVerification, getStats, getMappings, getHistory, getDbParks, getMatchReport, linkPark } from '../../services/parkVerificationService';
import { readExcelFromBase64 } from '../../services/excelService';
import { buildComparison, norm } from '../../utils/parkComparison';
import s from './ParkVerification.module.css';
import DataInventoryReport from './DataInventoryReport';

const API = process.env.REACT_APP_API_BASE_URL || '';
const GOOGLE_MAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';

const STATUS_LABELS = { verified: 'Onaylandi', pending: 'Bekliyor', rejected: 'Reddedildi', modified: 'Duzenlendi' };
const FILTERS = [
  { key: '', label: 'Tumu' },
  { key: 'pending', label: 'Bekleyen' },
  { key: 'verified', label: 'Onaylanan' },
  { key: 'modified', label: 'Duzenlenen' },
  { key: 'rejected', label: 'Reddedilen' },
];

const ParkVerification = () => {
  const [parks, setParks] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);

  const [viewMode, setViewMode] = useState('verify');
  const [mappings, setMappings] = useState([]);
  const [mappingsLoading, setMappingsLoading] = useState(false);
  const [mappingSearch, setMappingSearch] = useState('');
  const [expandedHistory, setExpandedHistory] = useState({});
  const [historyData, setHistoryData] = useState({});

  const [matchReport, setMatchReport] = useState(null);
  const [matchStats, setMatchStats] = useState(null);
  const [matchLoading, setMatchLoading] = useState(false);
  const [matchFilter, setMatchFilter] = useState('');
  const [matchSearch, setMatchSearch] = useState('');
  const [dbParksList, setDbParksList] = useState([]);
  const [dbSearch, setDbSearch] = useState('');
  const [selectedAski, setSelectedAski] = useState(null);
  const [selectedDb, setSelectedDb] = useState(null);
  const [linkSaving, setLinkSaving] = useState(false);

  const [selAboneno, setSelAboneno] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailTab, setDetailTab] = useState('compare');

  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [alert, setAlert] = useState(null);

  const [compData, setCompData] = useState(null);
  const [compLoading, setCompLoading] = useState(false);
  const [compSearch, setCompSearch] = useState('');
  const [compExpanded, setCompExpanded] = useState({});
  const [compMapPark, setCompMapPark] = useState(null);
  const compMapRef = useRef(null);
  const compMapInst = useRef(null);
  const compMarkerRef = useRef(null);

  const [drawMode, setDrawMode] = useState(null);
  const drawModeRef = useRef(null);
  const [polygonArea, setPolygonArea] = useState(null);
  const [parkSaving, setParkSaving] = useState(false);
  const [excelCache, setExcelCache] = useState(null);
  const excelCacheRef = useRef(null);
  const drawPointsRef = useRef([]);
  const drawMarkersRef = useRef([]);
  const drawPolylineRef = useRef(null);
  const drawPolygonRef = useRef(null);

  const mapRef = useRef(null);
  const mapInst = useRef(null);
  const markerRef = useRef(null);
  const polygonRef = useRef(null);
  const searchTimer = useRef(null);

  const loadParks = useCallback(async () => {
    try {
      setLoading(true);
      const params = { page, limit: 50 };
      if (search) params.search = search;
      if (filter) params.status = filter;
      const res = await getParks(params);
      setParks(res.parks || []);
      setTotal(res.total || 0);
    } catch (e) {
      console.error('Park listesi yuklenemedi:', e);
    } finally {
      setLoading(false);
    }
  }, [page, search, filter]);

  const loadStats = useCallback(async () => {
    try {
      const res = await getStats();
      setStats(res);
    } catch (e) { console.error('Istatistik yuklenemedi:', e); }
  }, []);

  useEffect(() => { loadParks(); }, [loadParks]);
  useEffect(() => { loadStats(); }, [loadStats]);

  useEffect(() => {
    if (excelCacheRef.current) return;
    (async () => {
      try {
        const raw = await readExcelFromBase64();
        const parkSheet = raw['PARK'] || [];
        const ep = [];
        parkSheet.forEach((row, idx) => {
          if (idx < 5) return;
          const sira = row[0], adi = String(row[1] || '').trim();
          if (sira && !isNaN(sira) && adi.length > 2) ep.push({ adi, normAdi: norm(adi) });
        });
        excelCacheRef.current = ep;
        setExcelCache(ep);
      } catch (e) { console.warn('Excel cache yuklenemedi:', e.message); }
    })();
  }, []);

  const hasExcelMatch = useCallback((parkName) => {
    const ep = excelCacheRef.current || excelCache;
    if (!ep || !parkName) return false;
    const n1 = norm(parkName);
    return ep.some(e => {
      if (e.normAdi === n1) return true;
      const w1 = n1.split(/\s+/), w2 = e.normAdi.split(/\s+/);
      const common = w1.filter(w => w.length > 2 && w2.includes(w)).length;
      return common / Math.max(w1.length, w2.length) >= 0.4;
    });
  }, [excelCache]);

  const loadMappings = useCallback(async () => {
    try {
      setMappingsLoading(true);
      const res = await getMappings(mappingSearch);
      setMappings(res.mappings || []);
    } catch (e) { console.error('Eslestirme yuklenemedi:', e); }
    finally { setMappingsLoading(false); }
  }, [mappingSearch]);

  useEffect(() => { if (viewMode === 'mappings') loadMappings(); }, [viewMode, loadMappings]);

  const loadMatchReport = useCallback(async () => {
    try {
      setMatchLoading(true);
      const [reportRes, dbRes] = await Promise.all([getMatchReport(), getDbParks()]);
      setMatchReport(reportRes.report || []);
      setMatchStats(reportRes.stats || null);
      setDbParksList(dbRes.parks || []);
    } catch (e) { console.error('Match report yuklenemedi:', e); }
    finally { setMatchLoading(false); }
  }, []);

  useEffect(() => { if (viewMode === 'matching') loadMatchReport(); }, [viewMode, loadMatchReport]);

  const loadComparison = useCallback(async () => {
    if (compData) return;
    setCompLoading(true);
    try {
      const [dbParks, askiRaw] = await Promise.all([
        fetch(`${API}/api/park-verification/db-parks?limit=9999`).then(r => r.json()).catch(() => ({ parks: [] })),
        fetch(`${API}/api/aski/analysis/query`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ groupBy: 'abone' }) }).then(r => r.json()).catch(() => ({ rows: [] })),
      ]);
      let excelParks = [];
      try {
        const raw = await readExcelFromBase64();
        const parkSheet = raw['PARK'] || [];
        parkSheet.forEach((row, idx) => {
          if (idx < 5) return;
          const sira = row[0], adi = String(row[1] || '').trim(), adres = String(row[2] || '').trim(), mahalle = String(row[3] || '').trim();
          if (sira && !isNaN(sira) && adi.length > 2) excelParks.push({ sira, adi, adres, mahalle });
        });
      } catch (e) { console.warn('Excel okunamadi:', e); }

      const comparison = buildComparison(askiRaw.rows || [], dbParks.parks || [], excelParks);
      setCompData(comparison);
    } catch (e) { console.error('Karsilastirma verisi yuklenemedi:', e); }
    finally { setCompLoading(false); }
  }, [compData]);

  useEffect(() => { if (viewMode === 'comparison') loadComparison(); }, [viewMode, loadComparison]);

  const filteredReport = (matchReport || []).filter(r => {
    if (matchFilter && r.status !== matchFilter) return false;
    if (matchSearch) {
      const s = matchSearch.toLowerCase();
      return (r.abone_adi || '').toLowerCase().includes(s) || (r.aboneno || '').includes(s) || (r.ilce || '').toLowerCase().includes(s) || (r.db_name || '').toLowerCase().includes(s);
    }
    return true;
  });

  const filteredDbParks = dbParksList.filter(d => {
    if (!dbSearch) return true;
    const s = dbSearch.toLowerCase();
    return (d.name_kmz || '').toLowerCase().includes(s) || (d.name_xls || '').toLowerCase().includes(s) || String(d.park_id).includes(s);
  });

  const handleLink = async () => {
    if (!selectedAski || !selectedDb) return;
    try {
      setLinkSaving(true);
      await linkPark({ aski_aboneno: selectedAski.aboneno, aski_abone_adi: selectedAski.abone_adi, db_park_id: selectedDb.park_id, ilce: selectedAski.ilce });
      setSelectedAski(null);
      setSelectedDb(null);
      loadMatchReport();
    } catch (e) { console.error('Eslestirme kaydedilemedi:', e); }
    finally { setLinkSaving(false); }
  };

  const FIELD_LABELS = { park_name: 'Park Adi', status: 'Durum', center_lat: 'Enlem', center_lng: 'Boylam',
    ilce: 'Ilce', mahalle: 'Mahalle', notes: 'Notlar', db_park_id: 'DB Park ID', verified_data: 'Dogrulama Verisi' };

  const toggleHistory = async (aboneno) => {
    if (expandedHistory[aboneno]) {
      setExpandedHistory(prev => ({ ...prev, [aboneno]: false }));
      return;
    }
    try {
      const res = await getHistory(aboneno);
      setHistoryData(prev => ({ ...prev, [aboneno]: res.history || [] }));
      setExpandedHistory(prev => ({ ...prev, [aboneno]: true }));
    } catch (e) { console.error('Gecmis yuklenemedi:', e); }
  };

  const handleSearch = (val) => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => { setSearch(val); setPage(0); }, 400);
  };

  const selectPark = async (aboneno) => {
    try {
      setSelAboneno(aboneno);
      setDetailLoading(true);
      setDetailTab('compare');
      setAlert(null);
      const res = await getParkDetail(aboneno);

      let excelMatch = null;
      try {
        const raw = await readExcelFromBase64();
        const parkSheet = raw['PARK'] || [];
        const excelParks = [];
        parkSheet.forEach((row, idx) => {
          if (idx < 5) return;
          const sira = row[0], adi = String(row[1] || '').trim(), adres = String(row[2] || '').trim(), mahalle = String(row[3] || '').trim();
          const cimAlan = parseFloat(row[4]) || null, sertZemin = parseFloat(row[5]) || null;
          const ioa = parseFloat(row[6]) || null, ciceklik = parseFloat(row[7]) || null;
          const yurumeYollari = parseFloat(row[8]) || null, pasifAlan = parseFloat(row[9]) || null;
          if (sira && !isNaN(sira) && adi.length > 2) excelParks.push({ sira, adi, adres, mahalle, cimAlan, sertZemin, ioa, ciceklik, yurumeYollari, pasifAlan });
        });
        const askiName = res.askiRecords?.[0]?.abone_adi || '';
        const dbName = res.dbPark?.name_kmz || res.dbPark?.name_xls || '';
        const searchName = askiName || dbName;
        if (searchName) {
          const n1 = norm(searchName);
          let bestScore = 0;
          excelParks.forEach(ep => {
            const n2 = norm(ep.adi);
            if (n1 === n2) { excelMatch = { ...ep, matchType: 'tam' }; bestScore = 999; return; }
            const words1 = n1.split(/\s+/), words2 = n2.split(/\s+/);
            const common = words1.filter(w => w.length > 2 && words2.includes(w)).length;
            const score = common / Math.max(words1.length, words2.length);
            if (score > bestScore && score >= 0.4) { bestScore = score; excelMatch = { ...ep, matchType: 'benzer', matchScore: Math.round(score * 100) }; }
          });
        }
        res.excelParks = excelParks;
      } catch (exErr) { console.warn('Excel okunamadi:', exErr.message); }

      res.excelMatch = excelMatch;
      setDetail(res);
      const aski = res.askiRecords?.[0] || {};
      const ver = res.verification || {};
      const db = res.dbPark || {};
      setForm({
        park_name: ver.park_name || aski.abone_adi || '',
        aski_aboneno: aboneno,
        aski_abone_adi: aski.abone_adi || '',
        ilce: ver.ilce || aski.ilce_adi || '',
        mahalle: ver.mahalle || aski.mahalle_adi || '',
        sokak: ver.sokak || aski.sokak_adi || '',
        db_park_id: ver.db_park_id || db.park_id || '',
        center_lat: ver.center_lat || db.latitude || '',
        center_lng: ver.center_lng || db.longitude || '',
        boundaries: ver.boundaries ? (typeof ver.boundaries === 'string' ? ver.boundaries : JSON.stringify(ver.boundaries)) : '',
        notes: ver.notes || '',
        status: ver.status || 'pending',
      });
    } catch (e) {
      console.error('Detay yuklenemedi:', e);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleSave = async (newStatus) => {
    try {
      setSaving(true);
      setAlert(null);
      let boundaries = form.boundaries;
      if (typeof boundaries === 'string' && boundaries.trim()) {
        try { boundaries = JSON.parse(boundaries); } catch { /* keep as string */ }
      }
      await saveVerification({
        ...form,
        park_name: form.park_name || form.aski_abone_adi || detail?.askiRecords?.[0]?.abone_adi || 'Isimsiz Park',
        aski_aboneno: form.aski_aboneno || selAboneno,
        park_id: detail?.park_id || detail?.id || form.db_park_id,
        center_lat: form.center_lat ? parseFloat(form.center_lat) : null,
        center_lng: form.center_lng ? parseFloat(form.center_lng) : null,
        boundaries: boundaries || null,
        area_m2: polygonArea || form.area_m2 || null,
        status: newStatus || form.status || 'verified',
        verified_by: 'admin',
      });
      setAlert({ type: 'success', msg: 'Dogrulama kaydedildi.' });
      loadParks();
      loadStats();
      selectPark(selAboneno);
    } catch (e) {
      setAlert({ type: 'error', msg: e.message });
    } finally {
      setSaving(false);
    }
  };

  // Google Maps
  const mapInitializedForRef = useRef(null);
  useEffect(() => { drawModeRef.current = drawMode; }, [drawMode]);
  useEffect(() => { mapFirstLoadDone.current = false; autoSearchDone.current = false; searchAutocompleteRef.current = null; drawPolygonRef.current = null; drawPointsRef.current = []; }, [detail]);

  useEffect(() => {
    if (detailTab !== 'map' || !GOOGLE_MAPS_KEY) return;

    const initMap = () => {
      if (!window.google?.maps || !mapRef.current) return;
      if (mapInst.current && mapInitializedForRef.current === mapRef.current) {
        window.google.maps.event.trigger(mapInst.current, 'resize');
        updateMap();
        searchAutocompleteRef.current = null;
        setTimeout(initPlaceSearch, 200);
        return;
      }
      mapInst.current = new window.google.maps.Map(mapRef.current, {
        center: { lat: 39.93, lng: 32.86 },
        zoom: 16,
        mapTypeId: 'hybrid',
        mapTypeControl: true,
        streetViewControl: false,
        disableDoubleClickZoom: true,
      });
      mapInitializedForRef.current = mapRef.current;
      mapInst.current.addListener('click', (e) => {
        if (drawModeRef.current === 'polygon') {
          addDrawPoint(e.latLng);
          return;
        }
        const lat = e.latLng.lat().toFixed(6);
        const lng = e.latLng.lng().toFixed(6);
        setForm(prev => ({ ...prev, center_lat: lat, center_lng: lng }));
      });
      mapInst.current.addListener('dblclick', (e) => {
        e.stop();
        if (drawModeRef.current === 'polygon') finishPolygon();
      });
      updateMap();
      searchAutocompleteRef.current = null;
      setTimeout(initPlaceSearch, 300);
    };

    const tryInit = () => {
      if (!mapRef.current) {
        setTimeout(tryInit, 100);
        return;
      }
      const existing = document.getElementById('gmap-park-ver');
      if (existing || window.google?.maps) {
        initMap();
      } else {
        const script = document.createElement('script');
        script.id = 'gmap-park-ver';
        script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_KEY}&libraries=drawing,geometry,places`;
        script.onload = () => setTimeout(initMap, 100);
        document.head.appendChild(script);
      }
    };

    const timer = setTimeout(tryInit, 50);
    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detailTab, detail]);

  const mapFirstLoadDone = useRef(false);
  const autoSearchDone = useRef(false);

  const autoSearchPark = useCallback(() => {
    if (!mapInst.current || !window.google?.maps?.places) return;
    if (autoSearchDone.current) return;

    const rawName = form.park_name || form.aski_abone_adi || '';
    if (!rawName) return;

    autoSearchDone.current = true;

    const cleaned = rawName
      .replace(/T\.?C\.?\s*/gi, '')
      .replace(/[A-ZÇĞİÖŞÜa-zçğıöşü]+\s*BLD\.?\s*/gi, '')
      .replace(/B[SŞ]K\.?\s*/gi, '')
      .replace(/PRK\.?\s*BA\.?\s*/gi, '')
      .replace(/H\.?\s*M[UÜ]D\.?\s*/gi, '')
      .replace(/M[UÜ]D\.?\s*/gi, '')
      .replace(/\bBA\b\.?\s*/gi, '')
      .replace(/\./g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

    const ilce = form.ilce || '';
    const mahalle = form.mahalle || '';
    const searchName = cleaned || rawName;
    const query = `${searchName} park ${mahalle} ${ilce} Ankara`.replace(/\s+/g, ' ').trim();

    const service = new window.google.maps.places.PlacesService(mapInst.current);
    service.textSearch(
      { query, type: 'park', region: 'tr' },
      (results, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK && results?.length > 0) {
          const place = results[0];
          const loc = place.geometry.location;
          if (markerRef.current) { markerRef.current.setMap(null); markerRef.current = null; }

          markerRef.current = new window.google.maps.Marker({
            position: loc,
            map: mapInst.current,
            draggable: true,
            title: place.name,
            animation: window.google.maps.Animation.DROP,
          });
          markerRef.current.addListener('dragend', (e) => {
            setForm(prev => ({ ...prev, center_lat: e.latLng.lat().toFixed(6), center_lng: e.latLng.lng().toFixed(6) }));
          });

          const infoWindow = new window.google.maps.InfoWindow({
            content: `<div style="font-size:13px"><strong>${place.name}</strong><br/><span style="color:#64748b">${place.formatted_address || ''}</span></div>`,
          });
          infoWindow.open(mapInst.current, markerRef.current);

          mapInst.current.panTo(loc);
          mapInst.current.setZoom(17);
          setForm(prev => ({ ...prev, center_lat: loc.lat().toFixed(6), center_lng: loc.lng().toFixed(6) }));
        } else {
          const geocoder = new window.google.maps.Geocoder();
          geocoder.geocode({ address: query }, (geoResults, geoStatus) => {
            if (geoStatus === 'OK' && geoResults?.length > 0) {
              const loc = geoResults[0].geometry.location;
              if (markerRef.current) { markerRef.current.setMap(null); markerRef.current = null; }
              markerRef.current = new window.google.maps.Marker({
                position: loc, map: mapInst.current, draggable: true, animation: window.google.maps.Animation.DROP,
              });
              markerRef.current.addListener('dragend', (e) => {
                setForm(prev => ({ ...prev, center_lat: e.latLng.lat().toFixed(6), center_lng: e.latLng.lng().toFixed(6) }));
              });
              mapInst.current.panTo(loc);
              mapInst.current.setZoom(16);
              setForm(prev => ({ ...prev, center_lat: loc.lat().toFixed(6), center_lng: loc.lng().toFixed(6) }));
            }
          });
        }
      }
    );
  }, [form.park_name, form.aski_abone_adi, form.ilce, form.mahalle]);

  const updateMap = useCallback(() => {
    if (!mapInst.current || !window.google?.maps) return;
    if (drawModeRef.current === 'polygon' || drawPointsRef.current.length > 0) return;

    if (markerRef.current) { markerRef.current.setMap(null); markerRef.current = null; }
    if (polygonRef.current) { polygonRef.current.setMap(null); polygonRef.current = null; }

    const lat = parseFloat(form.center_lat);
    const lng = parseFloat(form.center_lng);
    if (lat && lng && !isNaN(lat) && !isNaN(lng)) {
      const pos = { lat, lng };
      markerRef.current = new window.google.maps.Marker({ position: pos, map: mapInst.current, draggable: true });
      markerRef.current.addListener('dragend', (e) => {
        setForm(prev => ({ ...prev, center_lat: e.latLng.lat().toFixed(6), center_lng: e.latLng.lng().toFixed(6) }));
      });
      if (!mapFirstLoadDone.current) {
        mapInst.current.panTo(pos);
        mapInst.current.setZoom(16);
        mapFirstLoadDone.current = true;
      }
    }

    let bCoords = [];
    try {
      const raw = typeof form.boundaries === 'string' ? JSON.parse(form.boundaries) : form.boundaries;
      if (Array.isArray(raw)) bCoords = raw.map(c => ({ lat: c[0] || c.lat, lng: c[1] || c.lng }));
    } catch { /* ignore */ }

    if (bCoords.length > 2) {
      polygonRef.current = new window.google.maps.Polygon({
        paths: bCoords,
        map: mapInst.current,
        strokeColor: '#15803d',
        strokeWeight: 2,
        fillColor: '#22c55e',
        fillOpacity: 0.35,
        editable: true,
      });
      polygonRef.current.addListener('mouseup', () => {
        const path = polygonRef.current.getPath();
        const coords = [];
        for (let i = 0; i < path.getLength(); i++) {
          coords.push([path.getAt(i).lat(), path.getAt(i).lng()]);
        }
        setForm(prev => ({ ...prev, boundaries: JSON.stringify(coords) }));
      });
      const bounds = new window.google.maps.LatLngBounds();
      bCoords.forEach(c => bounds.extend(c));
      if (!mapFirstLoadDone.current) {
        mapInst.current.fitBounds(bounds);
        mapFirstLoadDone.current = true;
      }
    }

    if (!lat && !lng && bCoords.length === 0) {
      autoSearchPark();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.center_lat, form.center_lng, form.boundaries]);

  useEffect(() => {
    if (detailTab === 'map' && mapInst.current) updateMap();
  }, [detailTab, updateMap]);

  const clearDrawing = useCallback(() => {
    drawMarkersRef.current.forEach(m => m.setMap(null));
    drawMarkersRef.current = [];
    drawPointsRef.current = [];
    if (drawPolylineRef.current) { drawPolylineRef.current.setMap(null); drawPolylineRef.current = null; }
    if (drawPolygonRef.current) { drawPolygonRef.current.setMap(null); drawPolygonRef.current = null; }
    setPolygonArea(null);
  }, []);

  const finishPolygon = useCallback(() => {
    if (!mapInst.current || !window.google?.maps || drawPointsRef.current.length < 3) return;
    if (drawPolylineRef.current) { drawPolylineRef.current.setMap(null); drawPolylineRef.current = null; }
    drawMarkersRef.current.forEach(m => m.setMap(null));
    drawMarkersRef.current = [];

    if (drawPolygonRef.current) drawPolygonRef.current.setMap(null);
    const path = drawPointsRef.current;
    drawPolygonRef.current = new window.google.maps.Polygon({
      paths: path, map: mapInst.current, strokeColor: '#15803d', strokeWeight: 2, fillColor: '#22c55e', fillOpacity: 0.35, editable: true,
    });

    const coords = path.map(p => [p.lat, p.lng]);
    setForm(prev => ({ ...prev, boundaries: JSON.stringify(coords) }));

    const area = window.google.maps.geometry.spherical.computeArea(
      drawPolygonRef.current.getPath()
    );
    setPolygonArea(Math.round(area));

    drawPolygonRef.current.addListener('mouseup', () => {
      const updPath = drawPolygonRef.current.getPath();
      const updCoords = [];
      for (let i = 0; i < updPath.getLength(); i++) updCoords.push([updPath.getAt(i).lat(), updPath.getAt(i).lng()]);
      setForm(prev => ({ ...prev, boundaries: JSON.stringify(updCoords) }));
      const updArea = window.google.maps.geometry.spherical.computeArea(updPath);
      setPolygonArea(Math.round(updArea));
    });

    drawPointsRef.current = [];
    setDrawMode(null);
  }, []);

  const actuallyAddPoint = useCallback((pt) => {
    if (!mapInst.current || !window.google?.maps) return;
    drawPointsRef.current.push(pt);
    const isFirst = drawPointsRef.current.length === 1;

    const marker = new window.google.maps.Marker({
      position: pt, map: mapInst.current, icon: {
        path: window.google.maps.SymbolPath.CIRCLE,
        scale: isFirst ? 9 : 6,
        fillColor: isFirst ? '#ef4444' : '#22c55e',
        fillOpacity: 1,
        strokeColor: isFirst ? '#fff' : '#fff',
        strokeWeight: isFirst ? 3 : 2,
      },
      title: isFirst ? 'Baslangic noktasi (kapatmak icin tikla)' : undefined,
      zIndex: isFirst ? 999 : 1,
    });
    drawMarkersRef.current.push(marker);

    if (drawPolylineRef.current) drawPolylineRef.current.setMap(null);
    drawPolylineRef.current = new window.google.maps.Polyline({
      path: drawPointsRef.current, map: mapInst.current, strokeColor: '#22c55e', strokeWeight: 2, strokeOpacity: 0.8,
    });
  }, []);

  const addDrawPoint = useCallback((latLng) => {
    if (!mapInst.current || !window.google?.maps) return;
    const pt = { lat: latLng.lat(), lng: latLng.lng() };

    if (drawPointsRef.current.length >= 3) {
      const firstPt = drawPointsRef.current[0];
      const dist = window.google.maps.geometry.spherical.computeDistanceBetween(
        new window.google.maps.LatLng(pt.lat, pt.lng),
        new window.google.maps.LatLng(firstPt.lat, firstPt.lng)
      );
      const zoom = mapInst.current.getZoom() || 16;
      const snapMeters = Math.max(5, 200 / Math.pow(2, zoom - 14));
      if (dist < snapMeters) {
        finishPolygon();
        return;
      }
    }
    actuallyAddPoint(pt);
  }, [finishPolygon, actuallyAddPoint]);

  const saveParkPolygon = useCallback(async () => {
    if (!detail) { setAlert({ type: 'error', msg: 'Lutfen once bir park secin.' }); return; }
    if (!form.boundaries && !form.center_lat) { setAlert({ type: 'error', msg: 'Polygon cizin veya haritada bir konum secin.' }); return; }
    try {
      setParkSaving(true);
      let boundaries = form.boundaries;
      if (typeof boundaries === 'string' && boundaries.trim()) {
        try { boundaries = JSON.parse(boundaries); } catch { /* keep as string */ }
      }
      const payload = {
        park_name: form.park_name || form.aski_abone_adi || detail?.askiRecords?.[0]?.abone_adi || 'Isimsiz Park',
        aski_aboneno: form.aski_aboneno || selAboneno,
        park_id: detail.park_id || detail.id || form.db_park_id,
        center_lat: form.center_lat ? parseFloat(form.center_lat) : null,
        center_lng: form.center_lng ? parseFloat(form.center_lng) : null,
        boundaries: boundaries || null,
        area_m2: polygonArea,
        ilce: form.ilce || '',
        mahalle: form.mahalle || '',
        sokak: form.sokak || '',
        notes: form.notes || '',
        status: 'verified',
        verified_by: 'admin',
      };
      await saveVerification(payload);
      setAlert({ type: 'success', msg: 'Park bilgileri ve koordinatlar basariyla kaydedildi!' });
      loadParks();
      loadStats();
      if (selAboneno) selectPark(selAboneno);
    } catch (err) {
      setAlert({ type: 'error', msg: 'Kayit basarisiz: ' + (err.message || err) });
    } finally {
      setParkSaving(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detail, form, polygonArea, loadParks, loadStats, selAboneno]);

  const searchInputRef = useRef(null);
  const searchAutocompleteRef = useRef(null);

  const initPlaceSearch = useCallback(() => {
    if (!window.google?.maps?.places || !searchInputRef.current || !mapInst.current) return;
    if (searchAutocompleteRef.current) return;

    const autocomplete = new window.google.maps.places.Autocomplete(searchInputRef.current, {
      types: ['establishment', 'geocode'],
      componentRestrictions: { country: 'tr' },
    });
    autocomplete.bindTo('bounds', mapInst.current);
    autocomplete.addListener('place_changed', () => {
      const place = autocomplete.getPlace();
      if (!place.geometry?.location) return;
      const loc = place.geometry.location;
      if (markerRef.current) { markerRef.current.setMap(null); markerRef.current = null; }
      markerRef.current = new window.google.maps.Marker({
        position: loc, map: mapInst.current, draggable: true,
        title: place.name, animation: window.google.maps.Animation.DROP,
      });
      markerRef.current.addListener('dragend', (e) => {
        setForm(prev => ({ ...prev, center_lat: e.latLng.lat().toFixed(6), center_lng: e.latLng.lng().toFixed(6) }));
      });
      const infoWindow = new window.google.maps.InfoWindow({
        content: `<div style="font-size:13px"><strong>${place.name}</strong><br/><span style="color:#64748b">${place.formatted_address || ''}</span></div>`,
      });
      infoWindow.open(mapInst.current, markerRef.current);
      mapInst.current.panTo(loc);
      mapInst.current.setZoom(17);
      setForm(prev => ({ ...prev, center_lat: loc.lat().toFixed(6), center_lng: loc.lng().toFixed(6) }));
    });
    searchAutocompleteRef.current = autocomplete;
  }, []);

  const goToCompPark = (dbData) => {
    if (!dbData?.latitude || !dbData?.longitude) return;
    setCompMapPark(dbData);
  };

  useEffect(() => {
    if (!compMapPark || !compMapRef.current) return;
    if (!GOOGLE_MAPS_KEY) return;

    const initCompMap = () => {
      if (!window.google?.maps || !compMapRef.current) return;
      const pos = { lat: parseFloat(compMapPark.latitude), lng: parseFloat(compMapPark.longitude) };
      if (isNaN(pos.lat) || isNaN(pos.lng)) return;

      if (compMapInst.current) {
        if (compMarkerRef.current) { compMarkerRef.current.setMap(null); }
        compMapInst.current.panTo(pos);
        compMapInst.current.setZoom(17);
        compMarkerRef.current = new window.google.maps.Marker({ position: pos, map: compMapInst.current, title: compMapPark.name_kmz || compMapPark.name_xls || 'Park' });
        return;
      }

      compMapInst.current = new window.google.maps.Map(compMapRef.current, {
        center: pos,
        zoom: 17,
        mapTypeId: 'hybrid',
        mapTypeControl: true,
        streetViewControl: false,
      });
      compMarkerRef.current = new window.google.maps.Marker({ position: pos, map: compMapInst.current, title: compMapPark.name_kmz || compMapPark.name_xls || 'Park' });
    };

    const existingScript = document.getElementById('gmap-park-ver');
    if (existingScript || window.google?.maps) {
      setTimeout(initCompMap, 100);
    } else {
      const script = document.createElement('script');
      script.id = 'gmap-park-ver';
      script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_KEY}&libraries=drawing,geometry,places`;
      script.onload = () => setTimeout(initCompMap, 100);
      document.head.appendChild(script);
    }
  }, [compMapPark]);

  const totalPages = Math.ceil(total / 50);

  const renderCompare = () => {
    if (!detail) return null;
    const aski = detail.askiRecords?.[0] || {};
    const db = detail.dbPark || {};
    const ver = detail.verification || {};
    const ex = detail.excelMatch;

    const fields = [
      { label: 'Park Adi', askiVal: aski.abone_adi, dbVal: db.name_kmz || db.name_xls, exVal: ex?.adi, verVal: ver.park_name },
      { label: 'Ilce', askiVal: aski.ilce_adi, dbVal: null, exVal: null, verVal: ver.ilce },
      { label: 'Mahalle', askiVal: aski.mahalle_adi, dbVal: null, exVal: ex?.mahalle, verVal: ver.mahalle },
      { label: 'Adres', askiVal: aski.sokak_adi, dbVal: null, exVal: ex?.adres, verVal: ver.sokak },
      { label: 'Abone No', askiVal: aski.aboneno, dbVal: null, exVal: null, verVal: ver.aski_aboneno },
      { label: 'Abone Tipi', askiVal: aski.abonetipi, dbVal: null, exVal: null, verVal: null },
      { label: 'Tuketim (m3)', askiVal: aski.m3_tuketim, dbVal: null, exVal: null, verVal: null },
      { label: 'Donem', askiVal: aski.donem, dbVal: null, exVal: null, verVal: null },
      { label: 'Enlem', askiVal: null, dbVal: db.latitude, exVal: null, verVal: ver.center_lat },
      { label: 'Boylam', askiVal: null, dbVal: db.longitude, exVal: null, verVal: ver.center_lng },
    ];

    return (
      <div>
        {/* 3 Kaynak Karsilastirmasi */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginBottom: 20 }}>
          <div className={s.compareCard}>
            <div className={`${s.compareHeader} ${s.aski}`}>ASKI Canli Veri</div>
            <div className={s.compareBody}>
              {fields.map((f, i) => (
                <div key={i} className={s.compareRow}>
                  <span className={s.label}>{f.label}</span>
                  <span className={s.value}>{f.askiVal ?? '-'}</span>
                </div>
              ))}
            </div>
          </div>
          <div className={s.compareCard}>
            <div className={`${s.compareHeader} ${s.db}`}>Veritabani (DB/KMZ)</div>
            <div className={s.compareBody}>
              {fields.map((f, i) => {
                const match = f.askiVal && f.dbVal && norm(String(f.askiVal)) === norm(String(f.dbVal));
                const mismatch = f.askiVal && f.dbVal && !match;
                return (
                  <div key={i} className={s.compareRow}>
                    <span className={s.label}>{f.label}</span>
                    <span className={`${s.value} ${match ? s.match : ''} ${mismatch ? s.mismatch : ''}`}>
                      {f.dbVal ?? (f.verVal ?? '-')}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
          <div className={s.compareCard}>
            <div className={s.compareHeader} style={{ background: '#fef3c7', color: '#92400e' }}>
              Excel (2025.xlsx)
              {ex && <span style={{ marginLeft: 8, padding: '1px 8px', borderRadius: 12, fontSize: 10, fontWeight: 600, color: '#fff', background: ex.matchType === 'tam' ? '#059669' : '#d97706' }}>
                {ex.matchType === 'tam' ? 'Tam Eslesme' : `Benzer %${ex.matchScore}`}
              </span>}
            </div>
            <div className={s.compareBody}>
              {!ex ? (
                <div style={{ padding: 20, textAlign: 'center', color: '#94a3b8', fontSize: 12 }}>Excel eslesmesi bulunamadi</div>
              ) : (
                <>
                  {fields.map((f, i) => {
                    const match = f.askiVal && f.exVal && norm(String(f.askiVal)) === norm(String(f.exVal));
                    const mismatch = f.askiVal && f.exVal && !match;
                    return (
                      <div key={i} className={s.compareRow}>
                        <span className={s.label}>{f.label}</span>
                        <span className={`${s.value} ${match ? s.match : ''} ${mismatch ? s.mismatch : ''}`}>
                          {f.exVal ?? '-'}
                        </span>
                      </div>
                    );
                  })}
                </>
              )}
            </div>
          </div>
        </div>

        {/* Excel Alan Verileri */}
        {ex && (
          <div className={s.formSection} style={{ marginBottom: 20 }}>
            <div className={s.formTitle}>Excel Park Alan Detaylari</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10 }}>
              {[
                { label: 'Cim Alani', value: ex.cimAlan, color: '#16a34a', unit: 'm2' },
                { label: 'Sert Zemin', value: ex.sertZemin, color: '#94a3b8', unit: 'm2' },
                { label: 'Ibreli/Agac', value: ex.ioa, color: '#84cc16', unit: 'm2' },
                { label: 'Ciceklik', value: ex.ciceklik, color: '#ec4899', unit: 'm2' },
                { label: 'Yurume Yollari', value: ex.yurumeYollari, color: '#8b5cf6', unit: 'm2' },
                { label: 'Pasif Alan', value: ex.pasifAlan, color: '#f59e0b', unit: 'm2' },
              ].filter(f => f.value != null).map((f, i) => (
                <div key={i} style={{ background: '#f8fafc', borderRadius: 8, padding: '10px 14px', border: '1px solid #e2e8f0' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                    <div style={{ width: 10, height: 10, borderRadius: 3, background: f.color }} />
                    <span style={{ fontSize: 11, color: '#64748b' }}>{f.label}</span>
                  </div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: '#0f172a' }}>{parseFloat(f.value).toLocaleString('tr-TR')} {f.unit}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Gecmis ASKI Kayitlari */}
        {detail.askiRecords?.length > 1 && (
          <div className={s.formSection} style={{ marginBottom: 20 }}>
            <div className={s.formTitle}>Gecmis ASKI Kayitlari ({detail.askiRecords.length} donem)</div>
            <div style={{ maxHeight: 300, overflowY: 'auto' }}>
              <table className={s.historyTable}>
                <thead>
                  <tr><th>Donem</th><th>Tuketim (m3)</th><th>Kaynak</th><th>Sayac Turu</th><th>Cekim</th></tr>
                </thead>
                <tbody>
                  {detail.askiRecords.slice(0, 50).map((r, i) => (
                    <tr key={i}>
                      <td>{r.donem}</td>
                      <td>{r.m3_tuketim}</td>
                      <td>{r.kaynak}</td>
                      <td>{r.sayac_turu}</td>
                      <td>{r.fetch_source}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Veri Kaynaklari Ozet */}
        <div className={s.formSection}>
          <div className={s.formTitle}>Veri Kaynaklari Durumu</div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {[
              { label: 'ASKI Canli', ok: detail.askiRecords?.length > 0 },
              { label: 'DB Park', ok: !!detail.dbPark },
              { label: 'Excel Eslesmesi', ok: !!detail.excelMatch },
              { label: 'Alan Verisi', ok: !!detail.parkArea },
              { label: 'Sulama', ok: !!detail.parkIrrigation },
              { label: 'Abone/Sayac', ok: detail.parkSubscribers?.length > 0 },
              { label: 'Koordinat', ok: !!(detail.dbPark?.latitude || detail.verification?.center_lat) },
              { label: 'Dogrulama', ok: !!detail.verification },
            ].map((src, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 16, fontSize: 12, fontWeight: 600, background: src.ok ? '#dcfce7' : '#fee2e2', color: src.ok ? '#166534' : '#991b1b' }}>
                {src.ok ? '\u2713' : '\u2717'} {src.label}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const AREA_COLORS = ['#16a34a','#60a5fa','#f59e0b','#ef4444','#8b5cf6','#ec4899','#06b6d4','#84cc16'];
  const AREA_FIELDS = [
    { key: 'grass_area_m2', label: 'Cim Alani (m2)', color: '#16a34a' },
    { key: 'hard_surface_area_m2', label: 'Sert Zemin (m2)', color: '#94a3b8' },
    { key: 'playground_area_m2', label: 'Oyun Alani (m2)', color: '#f59e0b' },
    { key: 'flowerbed_area_m2', label: 'Ciceklik (m2)', color: '#ec4899' },
    { key: 'walking_paths_area_m2', label: 'Yuruyus Yolu (m2)', color: '#8b5cf6' },
    { key: 'pool_area_m2', label: 'Havuz (m2)', color: '#06b6d4' },
    { key: 'woodland_area_m2', label: 'Agaclik (m2)', color: '#84cc16' },
  ];

  const renderAreas = () => {
    if (!detail) return null;
    const area = detail.parkArea;
    const irr = detail.parkIrrigation;
    const subs = detail.parkSubscribers || [];
    const db = detail.dbPark;
    const ex = detail.excelMatch;

    const pieData = area ? AREA_FIELDS.filter(f => area[f.key] && parseFloat(area[f.key]) > 0)
      .map(f => ({ name: f.label, value: parseFloat(area[f.key]), color: f.color })) : [];

    const exPieData = ex ? [
      { name: 'Cim', value: ex.cimAlan, color: '#16a34a' },
      { name: 'Sert Zemin', value: ex.sertZemin, color: '#94a3b8' },
      { name: 'Agac', value: ex.ioa, color: '#84cc16' },
      { name: 'Cicek', value: ex.ciceklik, color: '#ec4899' },
      { name: 'Yurume', value: ex.yurumeYollari, color: '#8b5cf6' },
      { name: 'Pasif', value: ex.pasifAlan, color: '#f59e0b' },
    ].filter(f => f.value != null && f.value > 0) : [];

    return (
      <div>
        {/* DB + Excel Alan Karsilastirmasi */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
          {/* DB Alan */}
          <div className={s.formSection} style={{ margin: 0 }}>
            <div className={s.formTitle} style={{ color: '#047857' }}>DB Alan Verisi</div>
            {!area ? (
              <p style={{ color: '#94a3b8', fontSize: 13 }}>park_area tablosu bos.</p>
            ) : (
              <>
                <div style={{ marginBottom: 12 }}>
                  <span style={{ fontSize: 12, color: '#64748b' }}>Toplam Alan</span>
                  <div style={{ fontSize: 24, fontWeight: 700, color: '#0f172a' }}>{parseFloat(area.total_area_m2 || 0).toLocaleString('tr-TR')} m2</div>
                </div>
                {AREA_FIELDS.map(f => {
                  const val = parseFloat(area[f.key] || 0);
                  const pct = area.total_area_m2 ? (val / parseFloat(area.total_area_m2) * 100).toFixed(1) : 0;
                  return val > 0 ? (
                    <div key={f.key} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                      <div style={{ width: 10, height: 10, borderRadius: 3, background: f.color, flexShrink: 0 }} />
                      <span style={{ fontSize: 12, color: '#475569', flex: 1 }}>{f.label}</span>
                      <span style={{ fontSize: 12, fontWeight: 600, color: '#0f172a' }}>{val.toLocaleString('tr-TR')}</span>
                      <span style={{ fontSize: 11, color: '#94a3b8', width: 35, textAlign: 'right' }}>%{pct}</span>
                    </div>
                  ) : null;
                })}
                {pieData.length > 0 && (
                  <div style={{ width: '100%', height: 180, marginTop: 10 }}>
                    <ResponsiveContainer width="100%" height={180} minWidth={100} minHeight={100}>
                      <PieChart>
                        <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={70} label={({ name, percent }) => `${name.split(' ')[0]} %${(percent * 100).toFixed(0)}`} labelLine={false} fontSize={9}>
                          {pieData.map((d, i) => <Cell key={i} fill={d.color || AREA_COLORS[i % AREA_COLORS.length]} />)}
                        </Pie>
                        <Tooltip formatter={(v) => `${parseFloat(v).toLocaleString('tr-TR')} m2`} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </>
            )}
          </div>
          {/* Excel Alan */}
          <div className={s.formSection} style={{ margin: 0 }}>
            <div className={s.formTitle} style={{ color: '#92400e' }}>Excel Alan Verisi (2025.xlsx)</div>
            {!ex ? (
              <p style={{ color: '#94a3b8', fontSize: 13 }}>Excel eslesmesi bulunamadi.</p>
            ) : (
              <>
                <div style={{ marginBottom: 12 }}>
                  <span style={{ fontSize: 12, color: '#64748b' }}>Park</span>
                  <div style={{ fontSize: 14, fontWeight: 600, color: '#0f172a' }}>{ex.adi}</div>
                </div>
                {[
                  { label: 'Cim Alani', value: ex.cimAlan, color: '#16a34a' },
                  { label: 'Sert Zemin', value: ex.sertZemin, color: '#94a3b8' },
                  { label: 'Ibreli/Agac', value: ex.ioa, color: '#84cc16' },
                  { label: 'Ciceklik', value: ex.ciceklik, color: '#ec4899' },
                  { label: 'Yurume Yollari', value: ex.yurumeYollari, color: '#8b5cf6' },
                  { label: 'Pasif Alan', value: ex.pasifAlan, color: '#f59e0b' },
                ].filter(f => f.value != null).map((f, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                    <div style={{ width: 10, height: 10, borderRadius: 3, background: f.color, flexShrink: 0 }} />
                    <span style={{ fontSize: 12, color: '#475569', flex: 1 }}>{f.label}</span>
                    <span style={{ fontSize: 12, fontWeight: 600, color: '#0f172a' }}>{parseFloat(f.value).toLocaleString('tr-TR')} m2</span>
                  </div>
                ))}
                {exPieData.length > 0 && (
                  <div style={{ width: '100%', height: 180, marginTop: 10 }}>
                    <ResponsiveContainer width="100%" height={180} minWidth={100} minHeight={100}>
                      <PieChart>
                        <Pie data={exPieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={70} label={({ name, percent }) => `${name} %${(percent * 100).toFixed(0)}`} labelLine={false} fontSize={9}>
                          {exPieData.map((d, i) => <Cell key={i} fill={d.color} />)}
                        </Pie>
                        <Tooltip formatter={(v) => `${parseFloat(v).toLocaleString('tr-TR')} m2`} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Sulama */}
        <div className={s.formSection}>
          <div className={s.formTitle}>Sulama Bilgileri</div>
          {!irr ? (
            <p style={{ color: '#94a3b8', fontSize: 13 }}>Sulama verisi bulunamadi (park_irrigation tablosu bos).</p>
          ) : (
            <div className={s.formGrid}>
              <div className={s.compareRow}><span className={s.label}>Toplam Alan</span><span className={s.value}>{parseFloat(irr.area_m2 || 0).toLocaleString('tr-TR')} m2</span></div>
              <div className={s.compareRow}><span className={s.label}>Sulanan Alan</span><span className={s.value}>{parseFloat(irr.irrigated_area_m2 || 0).toLocaleString('tr-TR')} m2</span></div>
              <div className={s.compareRow}><span className={s.label}>Otomatik Sulama</span><span className={s.value}>%{irr.auto_irrigation_percent || 0}</span></div>
              <div className={s.compareRow}><span className={s.label}>Sulama Programi</span><span className={s.value}>{irr.irrigation_schedule || '-'}</span></div>
            </div>
          )}
        </div>

        {/* Aboneler & Sayaclar */}
        <div className={s.formSection}>
          <div className={s.formTitle}>Aboneler ve Sayaclar</div>
          {subs.length === 0 ? (
            <p style={{ color: '#94a3b8', fontSize: 13 }}>Bu park icin abone/sayac kaydı bulunamadi.</p>
          ) : (
            <table className={s.historyTable}>
              <thead><tr><th>Abone Adi</th><th>Abone No</th><th>Tip</th><th>Sayac No</th><th>Cap (mm)</th></tr></thead>
              <tbody>
                {subs.map((sub, i) => (
                  <tr key={i}><td>{sub.name_aski || '-'}</td><td>{sub.abone_no || '-'}</td><td>{sub.subscriber_type || '-'}</td><td>{sub.meter_no || '-'}</td><td>{sub.diameter || '-'}</td></tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* DB Konum */}
        {db && (
          <div className={s.formSection}>
            <div className={s.formTitle}>Veritabani Konum Bilgisi</div>
            <div className={s.formGrid}>
              <div className={s.compareRow}><span className={s.label}>KMZ Adi</span><span className={s.value}>{db.name_kmz || '-'}</span></div>
              <div className={s.compareRow}><span className={s.label}>Excel Adi</span><span className={s.value}>{db.name_xls || '-'}</span></div>
              <div className={s.compareRow}><span className={s.label}>Enlem</span><span className={s.value}>{db.latitude || '-'}</span></div>
              <div className={s.compareRow}><span className={s.label}>Boylam</span><span className={s.value}>{db.longitude || '-'}</span></div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const fmtPeriod = (d) => { const str = String(d); return str.length === 6 ? `${str.slice(4)}/${str.slice(0,4)}` : str; };

  const renderChart = () => {
    if (!detail) return null;
    const trend = detail.tuketimTrend || [];
    if (trend.length === 0) return <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>Tuketim verisi bulunamadi.</div>;

    const chartData = [...trend].reverse().map(t => ({ name: fmtPeriod(t.donem), m3: t.m3 }));
    const totalM3 = trend.reduce((a, t) => a + t.m3, 0);
    const avgM3 = totalM3 / trend.length;
    const maxM3 = Math.max(...trend.map(t => t.m3));
    const minM3 = Math.min(...trend.map(t => t.m3));

    return (
      <div>
        <div style={{ display: 'flex', gap: 16, marginBottom: 20, flexWrap: 'wrap' }}>
          {[
            { label: 'Toplam', value: `${totalM3.toLocaleString('tr-TR', { maximumFractionDigits: 0 })} m3`, color: '#1e40af' },
            { label: 'Ortalama', value: `${avgM3.toLocaleString('tr-TR', { maximumFractionDigits: 1 })} m3`, color: '#16a34a' },
            { label: 'Maksimum', value: `${maxM3.toLocaleString('tr-TR', { maximumFractionDigits: 1 })} m3`, color: '#dc2626' },
            { label: 'Minimum', value: `${minM3.toLocaleString('tr-TR', { maximumFractionDigits: 1 })} m3`, color: '#f59e0b' },
            { label: 'Donem Sayisi', value: trend.length, color: '#8b5cf6' },
          ].map((c, i) => (
            <div key={i} style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: '12px 18px', textAlign: 'center', minWidth: 120 }}>
              <div style={{ fontSize: 20, fontWeight: 700, color: c.color }}>{c.value}</div>
              <div style={{ fontSize: 11, color: '#64748b' }}>{c.label}</div>
            </div>
          ))}
        </div>

        <div className={s.formSection}>
          <div className={s.formTitle}>Donemsel Tuketim (m3) - Bar Grafik</div>
          <ResponsiveContainer width="100%" height={300} minWidth={200} minHeight={200}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="name" fontSize={10} angle={-35} textAnchor="end" height={55} />
              <YAxis fontSize={11} />
              <Tooltip formatter={(v) => `${parseFloat(v).toLocaleString('tr-TR')} m3`} />
              <Bar dataKey="m3" name="Tuketim (m3)" fill="#1e40af" radius={[4,4,0,0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className={s.formSection} style={{ marginTop: 16 }}>
          <div className={s.formTitle}>Trend Cizgisi</div>
          <ResponsiveContainer width="100%" height={250} minWidth={200} minHeight={150}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="name" fontSize={10} angle={-35} textAnchor="end" height={55} />
              <YAxis fontSize={11} />
              <Tooltip formatter={(v) => `${parseFloat(v).toLocaleString('tr-TR')} m3`} />
              <Line type="monotone" dataKey="m3" name="Tuketim (m3)" stroke="#16a34a" strokeWidth={2} dot={{ r: 4, fill: '#16a34a' }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    );
  };

  const renderMap = () => {
    const parkTitle = form.park_name || detail?.askiRecords?.[0]?.abone_adi || 'Park';
    return (
      <div className={s.mapModalOverlay} onClick={(e) => { if (e.target === e.currentTarget) setDetailTab('compare'); }}>
        <div className={s.mapModalContent}>
          {/* Header */}
          <div className={s.mapModalHeader}>
            <h3 className={s.mapModalTitle}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#1e40af" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6"/><line x1="8" y1="2" x2="8" y2="18"/><line x1="16" y1="6" x2="16" y2="22"/></svg>
              {parkTitle}
            </h3>
            <div className={s.mapHeaderActions}>
              <button className={`${s.mapActionBtn} ${drawMode === 'polygon' ? s.mapActionBtnActive : ''}`}
                onClick={() => { clearDrawing(); setDrawMode(drawMode === 'polygon' ? null : 'polygon'); }}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 013 3L7 19l-4 1 1-4L16.5 3.5z"/>
                </svg>
                Polygon Ciz
              </button>
              <button className={s.mapActionBtn} onClick={() => { clearDrawing(); setDrawMode(null); }}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/>
                </svg>
                Temizle
              </button>
              {polygonArea != null && (
                <span className={s.mapAreaBadge}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#166534" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2"/></svg>
                  {polygonArea.toLocaleString('tr-TR')} m&sup2;
                </span>
              )}
              {form.boundaries && (
                <button className={s.mapSaveBtn} onClick={saveParkPolygon} disabled={parkSaving}>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/>
                  </svg>
                  {parkSaving ? 'Kaydediliyor...' : 'Kaydet'}
                </button>
              )}
              <div className={s.mapSearchWrap}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                <input ref={searchInputRef} type="text" placeholder="Konum ara..." className={s.mapSearchInput} />
              </div>
              <button className={s.mapCloseBtn} onClick={() => setDetailTab('compare')} title="Kapat">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
          </div>

          {/* Draw hint */}
          {drawMode === 'polygon' && (
            <div className={s.mapDrawHint}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#0369a1" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
              <span>Haritada tiklayarak noktalar koyun <strong>({drawPointsRef.current.length} nokta)</strong>. Baslangic noktasina (kirmizi) tiklayarak kapatin.</span>
              <button onClick={finishPolygon} className={s.mapFinishBtn}>Tamamla</button>
            </div>
          )}

          {/* Map */}
          <div className={s.mapModalBody}>
            <div ref={mapRef} className={s.mapContainerModal}>
              {!GOOGLE_MAPS_KEY && <div className={s.loading}>Harita yuklenemiyor...</div>}
            </div>
          </div>

          {/* Footer - Save & Coordinates */}
          <div className={s.mapModalFooter}>
            {/* Park name edit row */}
            <div className={s.mapSaveRow}>
              <div className={s.mapSaveField}>
                <label className={s.mapSaveLabel}>Park Adi</label>
                <input
                  className={s.mapSaveInput}
                  value={form.park_name || ''}
                  onChange={e => setForm(prev => ({ ...prev, park_name: e.target.value }))}
                  placeholder="Park adini girin..."
                />
              </div>
              <div className={s.mapSaveField} style={{ maxWidth: 140 }}>
                <label className={s.mapSaveLabel}>Ilce</label>
                <input className={s.mapSaveInput} value={form.ilce || ''} onChange={e => setForm(prev => ({ ...prev, ilce: e.target.value }))} />
              </div>
              <div className={s.mapSaveField} style={{ maxWidth: 140 }}>
                <label className={s.mapSaveLabel}>Mahalle</label>
                <input className={s.mapSaveInput} value={form.mahalle || ''} onChange={e => setForm(prev => ({ ...prev, mahalle: e.target.value }))} />
              </div>
              <button className={s.mapSaveBtnLg} onClick={saveParkPolygon} disabled={parkSaving || (!form.boundaries && !form.center_lat)}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/>
                </svg>
                {parkSaving ? 'Kaydediliyor...' : 'Kaydet'}
              </button>
            </div>
            {alert && (
              <div className={`${s.mapAlert} ${alert.type === 'success' ? s.mapAlertSuccess : s.mapAlertError}`}>
                {alert.type === 'success' ? '\u2714' : '\u26A0'} {alert.msg}
              </div>
            )}
            {/* Coordinate info */}
            <div className={s.mapFooterGrid}>
              {form.center_lat && (
                <div className={s.mapFooterItem}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#6366f1" strokeWidth="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>
                  <span className={s.mapFooterLabel}>Enlem</span>
                  <span className={s.mapFooterValue}>{form.center_lat}</span>
                </div>
              )}
              {form.center_lng && (
                <div className={s.mapFooterItem}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#6366f1" strokeWidth="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>
                  <span className={s.mapFooterLabel}>Boylam</span>
                  <span className={s.mapFooterValue}>{form.center_lng}</span>
                </div>
              )}
              {polygonArea != null && (
                <div className={s.mapFooterItem}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2"/></svg>
                  <span className={s.mapFooterLabel}>Alan</span>
                  <span className={s.mapFooterValue} style={{ color: '#16a34a' }}>{polygonArea.toLocaleString('tr-TR')} m&sup2;</span>
                </div>
              )}
              {form.boundaries && (
                <div className={`${s.mapFooterItem} ${s.mapFooterCoords}`}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>
                  <span className={s.mapFooterLabel}>Polygon</span>
                  <span className={s.mapFooterValue} style={{ fontSize: 10, color: '#16a34a', fontWeight: 600 }}>
                    {(() => { try { const c = JSON.parse(form.boundaries); return `${c.length} nokta`; } catch { return 'Var'; } })()}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderVerifyForm = () => (
    <div>
      <div className={s.formSection}>
        <div className={s.formTitle}>Dogrulama Formu</div>
        <div className={s.formGrid}>
          <div className={s.formGroup}>
            <label>Park Adi (Onaylanan)</label>
            <input value={form.park_name || ''} onChange={e => setForm(prev => ({ ...prev, park_name: e.target.value }))} />
          </div>
          <div className={s.formGroup}>
            <label>ASKI Abone No</label>
            <input value={form.aski_aboneno || ''} readOnly style={{ background: '#f8fafc' }} />
          </div>
          <div className={s.formGroup}>
            <label>Ilce</label>
            <input value={form.ilce || ''} onChange={e => setForm(prev => ({ ...prev, ilce: e.target.value }))} />
          </div>
          <div className={s.formGroup}>
            <label>Mahalle</label>
            <input value={form.mahalle || ''} onChange={e => setForm(prev => ({ ...prev, mahalle: e.target.value }))} />
          </div>
          <div className={s.formGroup}>
            <label>Sokak</label>
            <input value={form.sokak || ''} onChange={e => setForm(prev => ({ ...prev, sokak: e.target.value }))} />
          </div>
          <div className={s.formGroup}>
            <label>DB Park ID (Eslestirme)</label>
            <input value={form.db_park_id || ''} onChange={e => setForm(prev => ({ ...prev, db_park_id: e.target.value }))} />
          </div>
          <div className={s.formGroup}>
            <label>Merkez Enlem</label>
            <input value={form.center_lat || ''} onChange={e => setForm(prev => ({ ...prev, center_lat: e.target.value }))} />
          </div>
          <div className={s.formGroup}>
            <label>Merkez Boylam</label>
            <input value={form.center_lng || ''} onChange={e => setForm(prev => ({ ...prev, center_lng: e.target.value }))} />
          </div>
        </div>
        <div className={s.formGroup} style={{ marginTop: 12 }}>
          <label>Notlar</label>
          <textarea value={form.notes || ''} onChange={e => setForm(prev => ({ ...prev, notes: e.target.value }))} placeholder="Ek notlar..." />
        </div>

        {/* Polygon & koordinat durumu */}
        <div className={s.polygonStatusBox}>
          <div className={s.polygonStatusTitle}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#1e40af" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6"/><line x1="8" y1="2" x2="8" y2="18"/><line x1="16" y1="6" x2="16" y2="22"/></svg>
            Harita & Polygon Bilgisi
          </div>
          <div className={s.polygonStatusGrid}>
            <div className={s.polygonStatusItem}>
              <span className={s.polygonStatusLabel}>Koordinat</span>
              <span className={form.center_lat && form.center_lng ? s.polygonStatusOk : s.polygonStatusMissing}>
                {form.center_lat && form.center_lng ? `${form.center_lat}, ${form.center_lng}` : 'Yok'}
              </span>
            </div>
            <div className={s.polygonStatusItem}>
              <span className={s.polygonStatusLabel}>Polygon</span>
              <span className={form.boundaries ? s.polygonStatusOk : s.polygonStatusMissing}>
                {form.boundaries
                  ? (() => { try { return `${JSON.parse(form.boundaries).length} nokta`; } catch { return 'Var'; } })()
                  : 'Cizilmedi'}
              </span>
            </div>
            <div className={s.polygonStatusItem}>
              <span className={s.polygonStatusLabel}>Alan</span>
              <span className={polygonArea != null ? s.polygonStatusOk : s.polygonStatusMissing}>
                {polygonArea != null ? `${polygonArea.toLocaleString('tr-TR')} m\u00B2` : 'Hesaplanmadi'}
              </span>
            </div>
          </div>
          {!form.boundaries && (
            <div className={s.polygonStatusHint}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
              Polygon cizmek icin Harita sekmesine gidin ve "Polygon Ciz" butonuna basin.
            </div>
          )}
        </div>
      </div>

      {alert && (
        <div className={`${s.alert} ${alert.type === 'success' ? s.alertSuccess : s.alertError}`}>{alert.msg}</div>
      )}

      <div className={s.btnRow}>
        <button className={`${s.btn} ${s.btnSuccess} ${saving ? s.btnDisabled : ''}`} onClick={() => handleSave('verified')}>
          Onayla
        </button>
        <button className={`${s.btn} ${s.btnPrimary} ${saving ? s.btnDisabled : ''}`} onClick={() => handleSave('modified')}>
          Duzenle ve Kaydet
        </button>
        <button className={`${s.btn} ${s.btnDanger} ${saving ? s.btnDisabled : ''}`} onClick={() => handleSave('rejected')}>
          Reddet
        </button>
      </div>
    </div>
  );

  return (
    <div className={s.container}>
      {/* View Mode Toggle */}
      <div className={s.viewModeBar}>
        <button className={`${s.viewModeBtn} ${viewMode === 'verify' ? s.active : ''}`}
          onClick={() => setViewMode('verify')}>Park Dogrulama</button>
        <button className={`${s.viewModeBtn} ${viewMode === 'comparison' ? s.active : ''}`}
          onClick={() => setViewMode('comparison')}>Karsilastirma</button>
        <button className={`${s.viewModeBtn} ${viewMode === 'mappings' ? s.active : ''}`}
          onClick={() => setViewMode('mappings')}>Eslestirme Tablosu</button>
        <button className={`${s.viewModeBtn} ${viewMode === 'matching' ? s.active : ''}`}
          onClick={() => setViewMode('matching')}>Eslestirme Yonetimi</button>
        <button className={`${s.viewModeBtn} ${viewMode === 'inventory' ? s.active : ''}`}
          onClick={() => setViewMode('inventory')}>Veri Envanteri</button>
      </div>

      {viewMode === 'comparison' ? (
        <div className={s.compPanel}>
          {compLoading ? (
            <div className={s.loading}>Karsilastirma verisi yukleniyor...</div>
          ) : !compData ? (
            <div className={s.emptyState}><h3>Veri yuklenemedi</h3></div>
          ) : (
            <>
              <div className={s.compStats}>
                <div className={s.compStatCard}><strong>{compData.askiTotal}</strong><span>ASKI Toplam</span></div>
                <div className={s.compStatCard}><strong>{compData.askiCankayaCount}</strong><span>ASKI Cankaya</span></div>
                <div className={s.compStatCard}><strong>{compData.dbTotal}</strong><span>DB Park</span></div>
                <div className={s.compStatCard}><strong>{compData.exTotal}</strong><span>Excel Park</span></div>
                <div className={`${s.compStatCard} ${s.compGreen}`}><strong>{compData.askiExExact}</strong><span>ASKI-Excel Tam</span></div>
                <div className={`${s.compStatCard} ${s.compAmber}`}><strong>{compData.askiExPartial}</strong><span>ASKI-Excel Benzer</span></div>
                <div className={`${s.compStatCard} ${s.compGreen}`}><strong>{compData.exDbExact}</strong><span>Excel-DB Tam</span></div>
                <div className={`${s.compStatCard} ${s.compAmber}`}><strong>{compData.exDbPartial}</strong><span>Excel-DB Benzer</span></div>
              </div>

              <div className={s.compSearchRow}>
                <input placeholder="Eslesmis parklarda ara..." value={compSearch} onChange={e => setCompSearch(e.target.value)} className={s.matchInput} />
              </div>

              <div className={s.compSection}>
                <div className={s.compSectionTitle}>ASKI - Excel Eslesmesi ({compData.askiExMatches.length})</div>
                <div className={s.compTableWrap}>
                  <table className={s.compTable}>
                    <thead>
                      <tr><th>ASKI Abone</th><th>Cikarilan Isim</th><th>Excel Park</th><th>Tur</th><th>Konum</th></tr>
                    </thead>
                    <tbody>
                      {compData.askiExMatches
                        .filter(m => !compSearch || norm(m.aski + ' ' + (m.extracted || '') + ' ' + (m.excelData?.adi || '')).includes(norm(compSearch)))
                        .map((m, i) => {
                          const dbMatch = compData.exDbMatches.find(em => norm(em.excel) === norm(m.excelData?.adi || ''));
                          const hasCoord = dbMatch?.dbData?.latitude && dbMatch?.dbData?.longitude;
                          return (
                            <React.Fragment key={i}>
                              <tr className={s.compRow} onClick={() => setCompExpanded(prev => ({ ...prev, [`ae_${i}`]: !prev[`ae_${i}`] }))}>
                                <td title={m.aski}>{(m.aski || '').substring(0, 40)}{(m.aski || '').length > 40 ? '...' : ''}</td>
                                <td style={{ fontWeight: 500 }}>{m.extracted}</td>
                                <td>{m.excelData?.adi || '-'}</td>
                                <td><span className={m.type === 'tam' ? s.matchBadgeGreen : s.matchBadgeBlue}>{m.type === 'tam' ? 'Tam' : 'Benzer'}</span></td>
                                <td>{hasCoord ? (
                                  <button className={s.mapGoBtn} onClick={(e) => { e.stopPropagation(); goToCompPark(dbMatch.dbData); }}>Haritada Goster</button>
                                ) : <span style={{ color: '#94a3b8', fontSize: 11 }}>Koordinat yok</span>}</td>
                              </tr>
                              {compExpanded[`ae_${i}`] && (
                                <tr><td colSpan={5} className={s.compDetailCell}>
                                  <div className={s.compDetailGrid}>
                                    <div>
                                      <div className={s.compDetailTitle}>ASKI Verileri</div>
                                      <div className={s.compDetailRow}><span>Abone No:</span> <strong>{m.askiData?.aboneno || '-'}</strong></div>
                                      <div className={s.compDetailRow}><span>Ilce:</span> {m.askiData?.ilce_adi || '-'}</div>
                                      <div className={s.compDetailRow}><span>Toplam Tuketim:</span> {m.askiData?.toplam_tuketim ? parseFloat(m.askiData.toplam_tuketim).toLocaleString('tr-TR') + ' m3' : '-'}</div>
                                      <div className={s.compDetailRow}><span>Donem:</span> {m.askiData?.ilk_donem || '?'} - {m.askiData?.son_donem || '?'}</div>
                                    </div>
                                    <div>
                                      <div className={s.compDetailTitle}>Excel Verileri</div>
                                      <div className={s.compDetailRow}><span>Adres:</span> {m.excelData?.adres || '-'}</div>
                                      <div className={s.compDetailRow}><span>Mahalle:</span> {m.excelData?.mahalle || '-'}</div>
                                      <div className={s.compDetailRow}><span>Sira:</span> {m.excelData?.sira || '-'}</div>
                                    </div>
                                    {dbMatch?.dbData && (
                                      <div>
                                        <div className={s.compDetailTitle}>DB Verileri</div>
                                        <div className={s.compDetailRow}><span>KMZ:</span> {dbMatch.dbData.name_kmz || '-'}</div>
                                        <div className={s.compDetailRow}><span>Koordinat:</span> {dbMatch.dbData.latitude}, {dbMatch.dbData.longitude}</div>
                                      </div>
                                    )}
                                  </div>
                                  {m.ortak && <div style={{ fontSize: 11, color: '#64748b', marginTop: 6 }}>Ortak Kelimeler: {m.ortak}</div>}
                                </td></tr>
                              )}
                            </React.Fragment>
                          );
                        })}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className={s.compSection}>
                <div className={s.compSectionTitle}>Excel - DB Eslesmesi ({compData.exDbMatches.length})</div>
                <div className={s.compTableWrap}>
                  <table className={s.compTable}>
                    <thead>
                      <tr><th>Excel Park</th><th>DB Park</th><th>Tur</th><th>Konum</th></tr>
                    </thead>
                    <tbody>
                      {compData.exDbMatches
                        .filter(m => !compSearch || norm(m.excel + ' ' + m.db).includes(norm(compSearch)))
                        .map((m, i) => {
                          const hasCoord = m.dbData?.latitude && m.dbData?.longitude;
                          return (
                            <React.Fragment key={i}>
                              <tr className={s.compRow} onClick={() => setCompExpanded(prev => ({ ...prev, [`ed_${i}`]: !prev[`ed_${i}`] }))}>
                                <td>{m.excel}</td>
                                <td style={{ fontWeight: 500 }}>{m.db}</td>
                                <td><span className={m.type === 'tam' ? s.matchBadgeGreen : s.matchBadgeBlue}>{m.type === 'tam' ? 'Tam' : 'Benzer'}</span></td>
                                <td>{hasCoord ? (
                                  <button className={s.mapGoBtn} onClick={(e) => { e.stopPropagation(); goToCompPark(m.dbData); }}>Haritada Goster</button>
                                ) : <span style={{ color: '#94a3b8', fontSize: 11 }}>Koordinat yok</span>}</td>
                              </tr>
                              {compExpanded[`ed_${i}`] && (
                                <tr><td colSpan={4} className={s.compDetailCell}>
                                  <div className={s.compDetailGrid}>
                                    <div>
                                      <div className={s.compDetailTitle}>Excel</div>
                                      <div className={s.compDetailRow}><span>Adres:</span> {m.excelData?.adres || '-'}</div>
                                      <div className={s.compDetailRow}><span>Mahalle:</span> {m.excelData?.mahalle || '-'}</div>
                                    </div>
                                    <div>
                                      <div className={s.compDetailTitle}>DB</div>
                                      <div className={s.compDetailRow}><span>KMZ:</span> {m.dbData?.name_kmz || '-'}</div>
                                      <div className={s.compDetailRow}><span>XLS:</span> {m.dbData?.name_xls || '-'}</div>
                                      <div className={s.compDetailRow}><span>Koordinat:</span> {m.dbData?.latitude || '-'}, {m.dbData?.longitude || '-'}</div>
                                    </div>
                                    {m.askiData && (
                                      <div>
                                        <div className={s.compDetailTitle}>ASKI</div>
                                        <div className={s.compDetailRow}><span>Abone:</span> {m.askiData.aboneno}</div>
                                        <div className={s.compDetailRow}><span>Tuketim:</span> {m.askiData.toplam_tuketim ? parseFloat(m.askiData.toplam_tuketim).toLocaleString('tr-TR') + ' m3' : '-'}</div>
                                      </div>
                                    )}
                                  </div>
                                  {m.ortak && <div style={{ fontSize: 11, color: '#64748b', marginTop: 6 }}>Ortak Kelimeler: {m.ortak}</div>}
                                </td></tr>
                              )}
                            </React.Fragment>
                          );
                        })}
                    </tbody>
                  </table>
                </div>
              </div>

              {compMapPark && (
                <div className={s.mapModalOverlay} onClick={(e) => { if (e.target === e.currentTarget) { setCompMapPark(null); compMapInst.current = null; compMarkerRef.current = null; } }}>
                  <div className={s.mapModalContent}>
                    <div className={s.mapModalHeader}>
                      <h3 className={s.mapModalTitle}>
                        &#128506; {compMapPark.name_kmz || compMapPark.name_xls || 'Park Konumu'}
                      </h3>
                      <button className={s.mapModalClose} onClick={() => { setCompMapPark(null); compMapInst.current = null; compMarkerRef.current = null; }}>&#10005;</button>
                    </div>
                    <div className={s.mapModalBody}>
                      {!GOOGLE_MAPS_KEY && (
                        <div className={`${s.alert} ${s.alertError}`}>
                          Google Maps API anahtari tanimli degil. .env.local dosyasina REACT_APP_GOOGLE_MAPS_API_KEY ekleyin.
                        </div>
                      )}
                      <div className={s.mapContainerModal} ref={compMapRef} />
                    </div>
                    <div className={s.mapModalFooter}>
                      <div className={s.formGrid}>
                        <div className={s.formGroup}>
                          <label>Enlem (Lat)</label>
                          <input type="text" value={compMapPark.latitude || ''} readOnly />
                        </div>
                        <div className={s.formGroup}>
                          <label>Boylam (Lng)</label>
                          <input type="text" value={compMapPark.longitude || ''} readOnly />
                        </div>
                        <div className={s.formGroup}>
                          <label>KMZ Adi</label>
                          <input type="text" value={compMapPark.name_kmz || '-'} readOnly />
                        </div>
                        <div className={s.formGroup}>
                          <label>XLS Adi</label>
                          <input type="text" value={compMapPark.name_xls || '-'} readOnly />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      ) : viewMode === 'matching' ? (
        <div className={s.matchingPanel}>
          {/* Stats bar */}
          {matchStats && (
            <div className={s.matchStatsBar}>
              <div className={`${s.matchStat}`}><strong>{matchStats.total}</strong><span>ASKI Park</span></div>
              <div className={`${s.matchStat} ${s.matchGreen}`}><strong>{matchStats.manual_matched}</strong><span>Manuel Eslesmis</span></div>
              <div className={`${s.matchStat} ${s.matchBlue}`}><strong>{matchStats.auto_matched}</strong><span>Oto. Eslesmis</span></div>
              <div className={`${s.matchStat} ${s.matchRed}`}><strong>{matchStats.no_match}</strong><span>Eslesmemis</span></div>
              <div className={`${s.matchStat} ${s.matchGray}`}><strong>{matchStats.db_total}</strong><span>DB Park</span></div>
            </div>
          )}

          {matchLoading ? (
            <div className={s.loading}>Eslestirme raporu yukleniyor...</div>
          ) : (
            <div className={s.matchLayout}>
              {/* Left: ASKI Parks */}
              <div className={s.matchCol}>
                <div className={s.matchColHeader}>
                  <strong>ASKI Parklari</strong>
                  <div className={s.matchFilters}>
                    <input placeholder="Ara..." value={matchSearch} onChange={e => setMatchSearch(e.target.value)} className={s.matchInput} />
                    <select value={matchFilter} onChange={e => setMatchFilter(e.target.value)} className={s.matchSelect}>
                      <option value="">Tumu ({matchReport?.length || 0})</option>
                      <option value="no_match">Eslesmemis ({matchStats?.no_match || 0})</option>
                      <option value="auto_matched">Oto. Eslesmis ({matchStats?.auto_matched || 0})</option>
                      <option value="manual_matched">Manuel ({matchStats?.manual_matched || 0})</option>
                    </select>
                  </div>
                </div>
                <div className={s.matchList}>
                  {filteredReport.map((r, i) => (
                    <div key={i} className={`${s.matchItem} ${selectedAski?.aboneno === r.aboneno ? s.matchSelected : ''} ${s['ms_' + r.status]}`}
                      onClick={() => setSelectedAski(r)}>
                      <div className={s.matchItemName}>{r.abone_adi || r.aboneno}</div>
                      <div className={s.matchItemMeta}>
                        {r.ilce || '-'} · {r.aboneno}
                        {r.status === 'auto_matched' && <span className={s.matchBadgeBlue}>Oto:{r.score}</span>}
                        {r.status === 'manual_matched' && <span className={s.matchBadgeGreen}>Manuel</span>}
                        {r.status === 'no_match' && <span className={s.matchBadgeRed}>Eslesmemis</span>}
                      </div>
                      {r.db_name && <div className={s.matchItemDb}>DB: {r.db_name}</div>}
                    </div>
                  ))}
                  {filteredReport.length === 0 && <div className={s.matchEmpty}>Sonuc bulunamadi</div>}
                </div>
              </div>

              {/* Center: Link action */}
              <div className={s.matchCenter}>
                {selectedAski && selectedDb ? (
                  <div className={s.linkBox}>
                    <div className={s.linkPreview}>
                      <div className={s.linkSide}>
                        <small>ASKI</small>
                        <strong>{selectedAski.abone_adi}</strong>
                        <span>{selectedAski.aboneno}</span>
                      </div>
                      <div className={s.linkArrow}>{'\u2194'}</div>
                      <div className={s.linkSide}>
                        <small>DB</small>
                        <strong>{selectedDb.name_kmz || selectedDb.name_xls}</strong>
                        <span>ID: {selectedDb.park_id}</span>
                      </div>
                    </div>
                    <button className={s.linkBtn} onClick={handleLink} disabled={linkSaving}>
                      {linkSaving ? 'Kaydediliyor...' : 'Eslestir ve Kaydet'}
                    </button>
                    <button className={s.linkCancelBtn} onClick={() => { setSelectedAski(null); setSelectedDb(null); }}>Iptal</button>
                  </div>
                ) : (
                  <div className={s.linkHint}>
                    {!selectedAski ? 'Soldan bir ASKI parki secin' : 'Sagdan bir DB parki secin'}
                  </div>
                )}
              </div>

              {/* Right: DB Parks */}
              <div className={s.matchCol}>
                <div className={s.matchColHeader}>
                  <strong>DB Parklari (water_billing)</strong>
                  <input placeholder="Ara (isim, ID)..." value={dbSearch} onChange={e => setDbSearch(e.target.value)} className={s.matchInput} />
                </div>
                <div className={s.matchList}>
                  {filteredDbParks.slice(0, 200).map((d, i) => (
                    <div key={i} className={`${s.matchItem} ${selectedDb?.park_id === d.park_id ? s.matchSelected : ''}`}
                      onClick={() => setSelectedDb(d)}>
                      <div className={s.matchItemName}>{d.name_kmz || d.name_xls || `Park #${d.park_id}`}</div>
                      <div className={s.matchItemMeta}>
                        ID: {d.park_id}
                        {d.latitude && d.longitude && ` · ${parseFloat(d.latitude).toFixed(4)}, ${parseFloat(d.longitude).toFixed(4)}`}
                      </div>
                    </div>
                  ))}
                  {filteredDbParks.length > 200 && <div className={s.matchEmpty}>+{filteredDbParks.length - 200} daha (arama ile daraltabilirsiniz)</div>}
                  {filteredDbParks.length === 0 && <div className={s.matchEmpty}>DB parki bulunamadi</div>}
                </div>
              </div>
            </div>
          )}
        </div>
      ) : viewMode === 'mappings' ? (
        <div className={s.mappingsPanel}>
          <div className={s.mappingsHeader}>
            <div>
              <h3 style={{ margin: 0, fontSize: '1.1rem', color: '#1e293b' }}>Dogrulanmis Park Eslestirmeleri</h3>
              <p style={{ margin: '4px 0 0', fontSize: '0.82rem', color: '#64748b' }}>
                ASKI'den gelen isimler ile dogrulanmis/degistirilmis isimler arasindaki eslestirme. Toplam: {mappings.length}
              </p>
            </div>
            <div className={s.mappingsSearchBox}>
              <input placeholder="Ara (isim, abone no, ilce)..." value={mappingSearch}
                onChange={e => setMappingSearch(e.target.value)} />
            </div>
          </div>
          {mappingsLoading ? (
            <div className={s.loading}>Yukleniyor...</div>
          ) : mappings.length === 0 ? (
            <div className={s.emptyState}>
              <h3>Henuz dogrulanmis park yok</h3>
              <p>"Park Dogrulama" gorunumunden parklari dogrulayarak eslestirme olusturabilirsiniz.</p>
            </div>
          ) : (
            <div className={s.mappingsTableWrap}>
              <table className={s.mappingsTable}>
                <thead>
                  <tr>
                    <th style={{ width: 50 }}>#</th>
                    <th>ASKI Abone Adi (Orijinal)</th>
                    <th style={{ width: 40, textAlign: 'center' }}></th>
                    <th>Dogrulanmis Isim</th>
                    <th>Abone No</th>
                    <th>Ilce</th>
                    <th>Durum</th>
                    <th>Tarih</th>
                  </tr>
                </thead>
                <tbody>
                  {mappings.map((m, i) => (
                    <React.Fragment key={m.id || i}>
                    <tr className={m.name_changed ? s.nameChanged : ''}>
                      <td style={{ color: '#94a3b8', fontSize: '0.8rem' }}>{i + 1}</td>
                      <td className={s.originalName}>{m.aski_abone_adi || '-'}</td>
                      <td style={{ textAlign: 'center', fontSize: '1.1rem', color: m.name_changed ? '#f59e0b' : '#94a3b8' }}>
                        {m.name_changed ? '\u2794' : '\u2713'}
                      </td>
                      <td className={s.verifiedName}>
                        {m.park_name || '-'}
                        {m.name_changed && <span className={s.changedBadge}>Degistirildi</span>}
                      </td>
                      <td style={{ fontFamily: 'monospace', fontSize: '0.82rem' }}>{m.aski_aboneno || '-'}</td>
                      <td>{m.ilce || '-'}</td>
                      <td>
                        <span className={`${s.parkStatus} ${s[m.status]}`}>
                          {m.status === 'verified' ? 'Onaylandi' : m.status === 'modified' ? 'Duzenlendi' : m.status}
                        </span>
                      </td>
                      <td style={{ fontSize: '0.8rem', color: '#64748b', display: 'flex', alignItems: 'center', gap: 6 }}>
                        {m.verified_at ? new Date(m.verified_at).toLocaleDateString('tr-TR') : '-'}
                        <button className={s.historyToggle} title="Degisiklik Gecmisi"
                          onClick={() => toggleHistory(m.aski_aboneno)}>
                          {expandedHistory[m.aski_aboneno] ? '\u25B2' : '\u25BC'}
                        </button>
                      </td>
                    </tr>
                    {expandedHistory[m.aski_aboneno] && (
                      <tr className={s.historyRow}>
                        <td colSpan={8}>
                          <div className={s.historyPanel}>
                            <strong style={{ fontSize: '0.82rem', color: '#475569' }}>Degisiklik Gecmisi</strong>
                            {(!historyData[m.aski_aboneno] || historyData[m.aski_aboneno].length === 0) ? (
                              <div style={{ fontSize: '0.8rem', color: '#94a3b8', marginTop: 4 }}>Gecmis kaydi yok</div>
                            ) : (
                              <table className={s.historySubTable}>
                                <thead>
                                  <tr>
                                    <th>Tarih</th>
                                    <th>Alan</th>
                                    <th>Eski Deger</th>
                                    <th></th>
                                    <th>Yeni Deger</th>
                                    <th>Yapan</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {historyData[m.aski_aboneno].map((h, hi) => (
                                    <tr key={hi}>
                                      <td>{new Date(h.changed_at).toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</td>
                                      <td style={{ fontWeight: 500 }}>{FIELD_LABELS[h.field_name] || h.field_name}</td>
                                      <td className={s.oldVal}>{h.old_value || '-'}</td>
                                      <td style={{ color: '#f59e0b' }}>{'\u2192'}</td>
                                      <td className={s.newVal}>{h.new_value || '-'}</td>
                                      <td style={{ color: '#64748b' }}>{h.changed_by || '-'}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div className={s.mappingsSummary}>
            <div className={s.summaryItem}>
              <strong>{mappings.filter(m => m.name_changed).length}</strong>
              <span>Isim Degistirilen</span>
            </div>
            <div className={s.summaryItem}>
              <strong>{mappings.filter(m => !m.name_changed).length}</strong>
              <span>Ayni Kalan</span>
            </div>
            <div className={s.summaryItem}>
              <strong>{mappings.filter(m => m.status === 'verified').length}</strong>
              <span>Onaylanan</span>
            </div>
            <div className={s.summaryItem}>
              <strong>{mappings.filter(m => m.status === 'modified').length}</strong>
              <span>Duzenlenen</span>
            </div>
          </div>
        </div>
      ) : viewMode === 'inventory' ? (
        <div style={{ flex: 1, overflow: 'auto', padding: 0 }}>
          <DataInventoryReport />
        </div>
      ) : (
        <div className={s.verifyLayout}>
          {/* Sidebar */}
          <div className={s.sidebar}>
            <div className={s.searchBox}>
              <input placeholder="Park ara (isim, abone no, ilce)..." onChange={e => handleSearch(e.target.value)} />
            </div>
            <div className={s.filterRow}>
              {FILTERS.map(f => (
                <button key={f.key} className={`${s.filterBtn} ${filter === f.key ? s.active : ''}`}
                  onClick={() => { setFilter(f.key); setPage(0); }}>{f.label}</button>
              ))}
            </div>
            <div className={s.parkList}>
              {loading ? (
                <div className={s.loading}>Yukleniyor...</div>
              ) : parks.length === 0 ? (
                <div className={s.loading}>Park bulunamadi</div>
              ) : parks.map((p, i) => (
                <div key={i} className={`${s.parkItem} ${selAboneno === p.aski.aboneno ? s.selected : ''}`}
                  onClick={() => selectPark(p.aski.aboneno)}>
                  <div className={s.parkName}>
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.aski.abone_adi || p.aski.aboneno}</span>
                    <span className={`${s.parkStatus} ${s[p.status]}`}>{STATUS_LABELS[p.status] || p.status}</span>
                  </div>
                  <div className={s.parkMeta}>
                    {p.aski.ilce_adi} · {p.aski.aboneno}
                    {p.dbPark ? ` · DB: ${p.dbPark.name_kmz || p.dbPark.name_xls || 'ID:' + p.dbPark.park_id}` : ''}
                  </div>
                  <div className={s.sourceBadges}>
                    <span className={`${s.badge} ${s.badgeAski}`} title="ASKI verisi mevcut">ASKI</span>
                    {p.dbPark && <span className={`${s.badge} ${s.badgeDb}`} title="Veritabani verisi mevcut">DB</span>}
                    {hasExcelMatch(p.aski.abone_adi) && <span className={`${s.badge} ${s.badgeExcel}`} title="Excel verisi mevcut">Excel</span>}
                    {p.dbPark?.latitude && p.dbPark?.longitude && <span className={`${s.badge} ${s.badgeCoord}`} title="Koordinat mevcut">&#128205;</span>}
                  </div>
                </div>
              ))}
            </div>
            {totalPages > 1 && (
              <div className={s.pagination}>
                <button disabled={page === 0} onClick={() => setPage(p => p - 1)}>Onceki</button>
                <span>{page + 1} / {totalPages}</span>
                <button disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}>Sonraki</button>
              </div>
            )}
            {stats && (
              <div className={s.statsBar}>
                <div className={`${s.statBox} ${s.blue}`}><strong>{stats.askiParkCount || 0}</strong><span>ASKI Park</span></div>
                <div className={`${s.statBox} ${s.green}`}><strong>{stats.verified || 0}</strong><span>Onaylanan</span></div>
                <div className={`${s.statBox} ${s.amber}`}><strong>{stats.pending || 0}</strong><span>Bekleyen</span></div>
                <div className={`${s.statBox} ${s.red}`}><strong>{stats.rejected || 0}</strong><span>Reddedilen</span></div>
              </div>
            )}
          </div>

          {/* Main area */}
          <div className={s.main}>
            {!selAboneno ? (
              <div className={s.emptyState}>
                <h3>Park Dogrulama</h3>
                <p>Soldaki listeden bir park secin. ASKI canli verileri ile mevcut veritabani verilerini karsilastirin,<br/>
                haritada konumunu isaretleyin ve dogrulayin.</p>
              </div>
            ) : detailLoading ? (
              <div className={s.loading}>Detaylar yukleniyor...</div>
            ) : (
              <div className={s.detailArea}>
                <div className={s.detailTabs}>
                  {['compare', 'areas', 'chart', 'map', 'verify'].map(t => (
                    <div key={t} className={`${s.detailTab} ${detailTab === t ? s.active : ''}`}
                      onClick={() => setDetailTab(t)}>
                      {{ compare: 'Karsilastirma', areas: 'Alan & Yesil', chart: 'Tuketim Grafigi', map: 'Harita', verify: 'Dogrulama' }[t]}
                    </div>
                  ))}
                </div>
                <div className={s.detailContent} style={detailTab === 'map' ? { padding: 0, display: 'flex', flexDirection: 'column' } : undefined}>
                  {detailTab === 'compare' && renderCompare()}
                  {detailTab === 'areas' && renderAreas()}
                  {detailTab === 'chart' && renderChart()}
                  {detailTab === 'map' && renderMap()}
                  {detailTab === 'verify' && renderVerifyForm()}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ParkVerification;
